import React, { useCallback, useState } from 'react'
import { Dialog, DialogActions, DialogContent } from '@material-ui/core'
import jsYaml from 'js-yaml'
import ValidatedForm from 'core/components/validatedForm/ValidatedForm'
import Button from 'core/elements/button'
import Text from 'core/elements/Text'
import useToggler from 'core/hooks/useToggler'
import { IRbacRoleBinding } from 'k8s/components/rbac/model'
import useEditBindingStyles from 'k8s/components/rbac/profiles/edit/useEditBindingsStyles'
import DisplayKeyValues from 'core/components/DisplayKeyValues'
import CodeMirror from 'core/components/validatedForm/CodeMirrorField'

interface Props {
  onClose: () => void
  open: boolean
  roleBinding: IRbacRoleBinding
}

const renderSubjectType = (type) => (subjects) => {
  const filteredSubjects = subjects.filter((subject) => subject?.kind === type)
  return (
    <>
      {filteredSubjects.map((subject) => (
        <Text variant="body2">{subject?.name}</Text>
      ))}
    </>
  )
}

const ViewRoleBindingDialog = ({ open, onClose, roleBinding }: Props) => {
  const classes = useEditBindingStyles()

  const keyValuePairs = [
    { key: 'Name', value: roleBinding?.metadata?.name },
    { key: 'Namespace', value: roleBinding?.metadata?.namespace },
    {
      key: roleBinding?.roleRef?.kind,
      value: roleBinding?.roleRef?.name,
    },
    {
      key: 'Service Accounts',
      value: roleBinding?.subjects,
      render: renderSubjectType('ServiceAccount'),
    },
    {
      key: 'Users',
      value: roleBinding?.subjects,
      render: renderSubjectType('User'),
    },
    {
      key: 'Groups',
      value: roleBinding?.subjects,
      render: renderSubjectType('Group'),
    },
    {
      key: 'External Users',
      value: roleBinding?.subjects,
      render: renderSubjectType('ExternalUser'),
    },
    {
      key: 'External Groups',
      value: roleBinding?.subjects,
      render: renderSubjectType('ExternalGroup'),
    },
  ]

  const [rawYaml, setRawYaml] = useState()
  const [showingYaml, , setShowingYaml] = useToggler()
  const showYaml = useCallback(() => {
    // Get document, or throw exception on error
    try {
      const rawYaml = jsYaml.dump(roleBinding)
      setRawYaml(rawYaml)
      setShowingYaml(true)
    } catch (e) {
      console.error(e)
    }
  }, [roleBinding])

  const exitYaml = useCallback(() => {
    setShowingYaml(false)
  }, [rawYaml])

  const defaultView = (
    <div className={classes.policyDetails}>
      <header className={classes.header}>
        <Text variant="body1">Role Binding Details</Text>
        <Button onClick={showYaml} className={classes.yamlBtn} variant="secondary">
          YAML View
        </Button>
      </header>
      <DisplayKeyValues keyValuePairs={keyValuePairs} />
    </div>
  )

  const yamlView = (
    <ValidatedForm onSubmit={exitYaml}>
      <div className={classes.filters}>
        <span>Role Binding YAML</span>
        <Button className={classes.yamlBtn} variant="secondary">
          Exit YAML View
        </Button>
      </div>
      <CodeMirror
        id="yamlView"
        label="YAML RESOURCE"
        value={rawYaml}
        className={classes.codeMirror}
      />
    </ValidatedForm>
  )

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogContent>{showingYaml ? yamlView : defaultView}</DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="secondary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ViewRoleBindingDialog
